import { useEffect, useRef } from 'react';
import { useDraggable } from "react-use-draggable-scroll";
import { isMobile } from '../../../utils';

import PropTypes from 'prop-types';

import {
    Stepper,
    Step,
    StepLabel,
    Button,
    MobileStepper,
    Typography,
    Grid,
    Box,
} from "@mui/material";

import { ButtonWithIcon } from "../../Common";
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import PriorityHighIcon from '@mui/icons-material/PriorityHigh';
import {
    Grey1,
    Grey3,
    White,
    chromeActiveHoverColor,
    chromeDividerColor,
    chromeMutedLabelColor,
    chromeMutedTextColor,
    trakrefBlue,
    red,
} from '../../../color_utils';
import { TranslateW } from "../../../Utils/Languages/Translate";

const stepperInactiveColor = chromeDividerColor;
const stepperInactiveTextColor = chromeMutedTextColor;
const stepperSkippedColor = Grey3;
const stepIconSize = 28;

const CompletedStepIcon = () => (
    <Box
        sx={{
            alignItems: "center",
            backgroundColor: White,
            borderRadius: "50%",
            display: "inline-flex",
            height: 17,
            justifyContent: "center",
            width: 17,
        }}
    >
        <CheckRoundedIcon sx={{ color: trakrefBlue, fontSize: 14 }} />
    </Box>
);

const StepIcon = ({ active, isComplete, isError, isCompletion, isSkipped, title }) => (
    <Box
        component="span"
        sx={{
            alignItems: "center",
            backgroundColor: isSkipped
                ? Grey3
                : active || isComplete ? trakrefBlue : White,
            border: `2px ${isSkipped ? "dashed" : "solid"} ${
                isSkipped
                    ? stepperSkippedColor
                    : isError
                        ? red
                        : active || isComplete ? trakrefBlue : stepperInactiveColor
            }`,
            borderRadius: "50%",
            color: isSkipped
                ? White
                : isError
                    ? red
                    : active || isComplete ? White : stepperInactiveTextColor,
            display: "inline-flex",
            fontFamily: "Open Sans Semibold",
            fontSize: isCompletion || isComplete ? "0px" : "14px",
            height: stepIconSize,
            justifyContent: "center",
            lineHeight: 1,
            opacity: isSkipped ? 0.7 : 1,
            position: "relative",
            transition: "background-color 120ms ease, border-color 120ms ease, transform 120ms ease",
            width: stepIconSize,
            zIndex: 1,
            ".MuiStep-root:hover &": {
                backgroundColor: isSkipped
                    ? Grey3
                    : active || isComplete ? chromeActiveHoverColor : White,
                borderColor: isSkipped
                    ? stepperSkippedColor
                    : isError
                        ? red
                        : active || isComplete ? chromeActiveHoverColor : stepperInactiveColor,
                color: isSkipped
                    ? White
                    : isError
                        ? red
                        : active || isComplete ? White : stepperInactiveTextColor,
            },
            ".MuiStep-root:active &": {
                transform: isSkipped ? "none" : "scale(0.94)",
            },
        }}
    >
        {isCompletion ? (
            <LockOutlinedIcon sx={{ color: "inherit", fontSize: 16 }} />
        ) : isComplete ? (
            <CompletedStepIcon />
        ) : isError ? (
            <PriorityHighIcon sx={{ color: "inherit", fontSize: 16 }} />
        ) : title}
    </Box>
);

const stepperLineColor = stepperInactiveColor;
const stepperLabelWidth = 130;
const connectorGapFromCenter = 35;
const edgeConnectorOffset = stepIconSize / 2 + connectorGapFromCenter;
const getStepId = (step) => step?.props?.id;
const isCompletionStep = (step) => getStepId(step) === "Completion";

const getStepState = (steps, stepCompleted, stepErrors = [], activeStep, index, skippedStepIds = []) => {
    const step = steps[index];
    const isActive = activeStep === index;
    const completionStep = isCompletionStep(step);
    const isSkipped = !!step && skippedStepIds.includes(getStepId(step));
    const isComplete = stepCompleted[index] && !isActive && !completionStep && !isSkipped;
    const isError = !!stepErrors[index] && !isActive && !completionStep && !isSkipped && !isComplete;

    return {
        isActive,
        isComplete,
        isError,
        isCompletion: completionStep,
        isSkipped,
        step,
    };
};

const getConnectorStyle = (steps, stepCompleted, stepErrors = [], activeStep, index, skippedStepIds = []) => {
    const currentStep = getStepState(steps, stepCompleted, stepErrors, activeStep, index, skippedStepIds);
    const nextStep = getStepState(steps, stepCompleted, stepErrors, activeStep, index + 1, skippedStepIds);

    const color = currentStep.isComplete && (nextStep.isComplete || nextStep.isActive)
        ? trakrefBlue
        : stepperLineColor;
    const style = currentStep.isSkipped || nextStep.isSkipped ? "dashed" : "solid";

    return { color, style };
};

const DesktopStepper = ({
    activeStep,
    hideStepCount,
    onStepChange,
    stepCompleted,
    stepErrors,
    skippedStepIds,
    steps,
    sx,
}) => {
    const ref = useRef();
    const activeStepRef = useRef();
    const { events } = useDraggable(ref);

    useEffect(() => {
        activeStepRef.current?.scrollIntoView({
            behavior: "smooth",
            block: "nearest",
            inline: "center"
        });
    }, [activeStep]);

    const onHandleStepClick = (e, index) => {
        e.stopPropagation();
        const stepId = steps[index]?.props?.id;
        if (skippedStepIds && skippedStepIds.includes(stepId)) return;
        onStepChange(index);
    };

    return (
        <Stepper
            id="stepper-container"
            alternativeLabel
            nonLinear
            activeStep={activeStep}
            connector={null}
            sx={{
                "& .MuiStepLabel-iconContainer": {
                    display: hideStepCount ? "none" : "flex",
                    justifyContent: "center",
                    paddingRight: "0px",
                    position: "relative",
                    zIndex: 1,
                },
                "& .MuiStepLabel-labelContainer": {
                    width: stepperLabelWidth,
                },
                "& .MuiStepLabel-root": {
                    width: "100%",
                },
                "&::after": {
                    backgroundColor: stepperLineColor,
                    bottom: 0,
                    content: '""',
                    height: "1px",
                    left: 0,
                    position: "absolute",
                    right: 0,
                },
                minHeight: 90,
                overflowX: "hidden",
                padding: "16px 30px 16px",
                position: "relative",
                ...sx,
            }}
            {...events}
            ref={ref}
        >
            {
                steps.map((step, index) => {
                    const { isActive, isComplete, isError, isCompletion, isSkipped } = getStepState(
                        steps,
                        stepCompleted,
                        stepErrors,
                        activeStep,
                        index,
                        skippedStepIds
                    );
                    const { color: connectorColor, style: connectorStyle } = getConnectorStyle(
                        steps,
                        stepCompleted,
                        stepErrors,
                        activeStep,
                        index,
                        skippedStepIds
                    );

                    return (
                        <Step
                            key={`${step}-${index}`}
                            sx={{
                                cursor: isSkipped ? "not-allowed" : "pointer",
                                flex: "1 1 0",
                                minWidth: 0,
                                paddingLeft: "0px",
                                paddingRight: "0px",
                                position: "relative",
                                ...(index < steps.length - 1 && {
                                    "&::after": {
                                        borderTop: `2px ${connectorStyle} ${connectorColor}`,
                                        content: '""',
                                        left: index === 0 ? `${edgeConnectorOffset}px` : `calc(50% + ${connectorGapFromCenter}px)`,
                                        position: "absolute",
                                        right: index === steps.length - 2 ? `calc(-100% + ${edgeConnectorOffset}px)` : `calc(-50% + ${connectorGapFromCenter}px)`,
                                        top: `${stepIconSize / 2}px`,
                                        zIndex: 0,
                                    },
                                }),
                                ...(index === 0 && {
                                    "& .MuiStepLabel-root": {
                                        alignItems: "flex-start",
                                    },
                                    "& .MuiStepLabel-labelContainer": {
                                        marginLeft: `-${(stepperLabelWidth - stepIconSize) / 2}px`,
                                    },
                                }),
                                ...(index === steps.length - 1 && {
                                    "& .MuiStepLabel-root": {
                                        alignItems: "flex-end",
                                    },
                                    "& .MuiStepLabel-labelContainer": {
                                        marginRight: `-${(stepperLabelWidth - stepIconSize) / 2}px`,
                                    },
                                }),
                                '&:active .MuiButton-root': {
                                    transform: isSkipped ? "none" : "scale(0.96)",
                                },
                            }}
                            onClick={(e) => onHandleStepClick(e, index)}
                            title={isSkipped ? "This step was skipped" : undefined}
                        >
                            <StepLabel
                                icon={
                                    <StepIcon
                                        active={isActive}
                                        isComplete={isComplete}
                                        isError={isError}
                                        isCompletion={isCompletion}
                                        isSkipped={isSkipped}
                                        title={`${index + 1}`}
                                    />
                                }
                                sx={{
                                    "& .MuiStepLabel-label": {
                                        marginTop: "6px !important",
                                    },
                                }}
                            >
                                <Button
                                    ref={isActive ? activeStepRef : null}
                                    disableRipple={isSkipped}
                                    sx={{
                                        "&.MuiButton-root": {
                                            alignItems: "flex-start",
                                            color: `${
                                                isSkipped
                                                    ? stepperSkippedColor
                                                    : isError
                                                        ? red
                                                        : isActive || isComplete ? Grey1 : chromeMutedLabelColor
                                            } !important`,
                                            display: "block",
                                            fontFamily: "Open Sans",
                                            fontSize: "11px",
                                            fontWeight: 400,
                                            lineHeight: 1.15,
                                            minWidth: 0,
                                            padding: "0px 4px",
                                            overflowWrap: "anywhere",
                                            textAlign: "center",
                                            transition: "color 120ms ease, transform 120ms ease",
                                            whiteSpace: "normal",
                                            width: "100%",
                                            wordBreak: "break-word",
                                        },
                                        ":focus": {
                                            outline: "none"
                                        },
                                        "&:hover": {
                                           background: 'transparent'
                                        },
                                        textTransform: 'none',
                                    }}
                                >
                                    {step}
                                    {isSkipped && (
                                        <Typography
                                            component="span"
                                            sx={{
                                                color: stepperSkippedColor,
                                                display: "block",
                                                fontFamily: "Open Sans",
                                                fontSize: "10px",
                                                fontStyle: "italic",
                                                lineHeight: 1.1,
                                                marginTop: "2px",
                                            }}
                                        >
                                            Skipped
                                        </Typography>
                                    )}
                                </Button>
                            </StepLabel>
                        </Step>
                    );
                })
            }
        </Stepper>
    );
};

const CustomStepper = ({
    steps,
    onStepChange,
    sx,
    activeStep,
    hideStepCount,
    stepCompleted,
    stepErrors,
    skippedStepIds,
}) => {
    // Mobile Back/Next are sequential, so they must step OVER skipped steps (e.g. Gas Transaction
    // when gas transfer is disabled) rather than stalling on the hidden neighbor — matching the
    // wizard's bottom Back/Next, which walk past skipped steps via handleStepChange (#15484). Walk
    // in the direction of travel to the next navigable step; bail only if none remains.
    const onHandleStepClick = (e, index) => {
        e.stopPropagation();
        const direction = index > activeStep ? 1 : index < activeStep ? -1 : 0;
        let target = index;
        while (
            direction !== 0
            && target >= 0
            && target < steps.length
            && skippedStepIds.includes(steps[target]?.props?.id)
        ) {
            target += direction;
        }
        if (target < 0 || target >= steps.length) return;
        if (skippedStepIds.includes(steps[target]?.props?.id)) return;
        onStepChange(target);
    };

    if (isMobile) {
        const backDisabled = activeStep === 0;
        const nextDisabled = activeStep + 1 === steps.length;
        const renderNextButton = steps.length === 1 || activeStep < steps.length - 1;
        // Bug 16743: steps can be plain strings (AddLocation, UserForm, Audit,
        // SiteAir), not just elements with a translation id — `.props.id`
        // threw and white-screened every string-steps wizard on mobile.
        const stepId = steps[activeStep]?.props?.id;
        const title = stepId ? TranslateW(stepId) : steps[activeStep];
        const boxStyle = { width: 75, display: "flex", justifyContent: "flex-start" };
        return (
            <Grid container direction="column" sx={sx}>
                <Grid item xs={12}>
                    <MobileStepper
                        variant="text"
                        position={"static"}
                        activeStep={activeStep}
                        sx={{ backgroundColor: "transparent", mt: 3, mb: 3 }}
                        backButton={
                            <Box sx={{...boxStyle}}>
                                {!backDisabled ? (
                                    <ButtonWithIcon
                                        buttonText={"Back"}
                                        variant={"text"}
                                        onClick={(e) => onHandleStepClick(e, activeStep - 1)}
                                        Icon={ArrowBackIosIcon}
                                        disabled={backDisabled}
                                        color={`${backDisabled ? Grey3 : trakrefBlue} !important`}
                                    />
                                ) : (
                                    <Box sx={{ height: 32 }} />
                                )}
                            </Box>
                        }
                        nextButton={
                            <Box sx={{...boxStyle}}>
                                {renderNextButton ? (
                                    <ButtonWithIcon
                                        buttonText={"Next"}
                                        variant={"text"}
                                        onClick={(e) => onHandleStepClick(e, activeStep + 1)}
                                        Icon={ArrowForwardIosIcon}
                                        iconPosition={"end"}
                                        disabled={nextDisabled}
                                        color={`${nextDisabled ? Grey3 : trakrefBlue} !important`}
                                    />
                                ) : (
                                    <Box sx={{ height: 32 }} />
                                )}
                            </Box>
                        }
                        steps={steps.length}
                    />
                </Grid>
                <Grid item xs={12} sx={{ justifyContent: "flex-start" }} mb={2}>
                    <Typography variant="h6" sx={{ color: Grey1, fontSize: "14px" }}>
                        {`${activeStep + 1}. ${title}`}
                    </Typography>
                </Grid>
            </Grid>
        );
    }

    return (
        <DesktopStepper
            activeStep={activeStep}
            hideStepCount={hideStepCount}
            onStepChange={onStepChange}
            stepCompleted={stepCompleted}
            stepErrors={stepErrors}
            skippedStepIds={skippedStepIds}
            steps={steps}
            sx={sx}
        />
    );
};

CustomStepper.propTypes = {
    activeStep: PropTypes.number.isRequired,
    steps: PropTypes.array.isRequired,
    onStepChange: PropTypes.func.isRequired,
    hideStepCount: PropTypes.bool,
    sx: PropTypes.object,
    stepCompleted: PropTypes.array,
    stepErrors: PropTypes.array,
    skippedStepIds: PropTypes.arrayOf(PropTypes.string),
};

CustomStepper.defaultProps = {
    sx: {},
    hideStepCount: false,
    stepCompleted: [],
    stepErrors: [],
    skippedStepIds: [],
};

export default CustomStepper;
